import { Box, SxProps, TextField, Theme, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CLIENTS_ENDPOINT } from "../../consts";
import { formatAddress, highlightMatch } from "../../utils";
import CustomAutocomplete, {
  OptionRecord,
} from "../ui/customAutocomplete/customAutocomplete.component";

interface ClientAutocompleteProps {
  value: OptionRecord | null;
  onChange: (value: OptionRecord | null) => void;
  label: string;
  required?: boolean;
  error?: string | null;
  sx?: SxProps<Theme>;
}

/**
 * Picks a client by name, searched on the server. Mirrors the job form's
 * client field: each option shows the client's address under the name, with
 * the search term highlighted in both, so clients with similar names can be
 * told apart.
 */
export default function ClientAutocomplete({
  value,
  onChange,
  label,
  required = false,
  error,
  sx,
}: ClientAutocompleteProps) {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState<string>("");

  return (
    <CustomAutocomplete
      size="small"
      value={value}
      onInputChange={(_event, input, reason) => {
        if (reason === "input") {
          setSearchInput(input);
        }
      }}
      onChange={(_event, selected) => onChange(selected)}
      apiConfig={{
        url: CLIENTS_ENDPOINT,
        minSearchChars: 2,
        debounceMs: 300,
      }}
      // Required: without it MUI hands the whole client object to React as a
      // child and the render throws.
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      noOptionsText={t("No options")}
      sx={sx}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={!!error}
          helperText={error}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...otherProps } = props;
        const address = formatAddress(option.address || null);

        return (
          <Box
            component="li"
            key={key}
            {...otherProps}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, textAlign: "center" }}
            >
              {highlightMatch(option.name, searchInput)}
            </Typography>
            {address && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.25, textAlign: "center" }}
              >
                {highlightMatch(address, searchInput)}
              </Typography>
            )}
          </Box>
        );
      }}
    />
  );
}
